import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  inject,
  signal,
  computed,
  input,
  output,
  viewChildren,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { BehaviorSubject, ReplaySubject, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import UserUtil, { formatUserName } from 'app/core/utilities/user-util';
import { SnackBarComponent, SnackBarData } from 'app/shared/snackbar/snackbar.component';
import { AppState, Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import RingColor from 'entity/RingColor';
import User from 'entity/User';
import { BaseUser, MirrorType, Presenter, PresenterType } from 'messages/websocket.messages';

import { StageSizeType } from '../../performer-participant/performer-participant.types';
import { BadgingService } from '../../services/badging/badging.service';
import { BrowserService } from '../../services/browser.service';
import { DeviceService } from '../../services/device/device.service';
import { EmoteService, Emoticon, UserEmote } from '../../services/emote/emote.service';
import { FavoriteService } from '../../services/favorite.service';
import { GalleryService } from '../../services/gallery/gallery.service';
import { LivestreamService } from '../../services/livestream/livestream.service';
import { LivestreamMediaStreams } from '../../services/livestream/livestream.types';
import { PresentationService, MirrorMediaStreams } from '../../services/presentation/presentation.service';
import { createMirrorId } from '../../services/presentation/util';
import { VideoRoomService } from '../../services/video-room.service';
import { socialAppSelectors, ExtendedPresenter, socialAppActions, PopOutState } from '../../state/app';
import { PresentationPresenterSharingComponent } from '../presentation-presenter-sharing/presentation-presenter-sharing.component';
import { PresentationThumbprintTypes } from '../presentation-thumbprint/presentation-thumbprint.component';

export interface PresenterSlot {
  isFavorite?: boolean;
  videoId: string;
  name?: string;
  userId: string;
  inUse: boolean;
  isLocal: boolean;
  ringColor?: string;
  videoMuted: boolean;
  audioMuted: boolean;
  isLiveStream: boolean;
  emote: Emoticon | undefined;
}

export type PresenterNameDisplay = 'always' | 'never' | 'hover';
export type PresenterNamePosition = 'bottom' | 'top';

export const EMPTY_SLOTS: PresenterSlot[] = [
  {
    videoId: 'presenter1',
    inUse: false,
    isLocal: false,
    videoMuted: false,
    audioMuted: false,
    isLiveStream: false,
    emote: undefined,
    userId: '',
  },
  {
    videoId: 'presenter2',
    inUse: false,
    isLocal: false,
    videoMuted: false,
    audioMuted: false,
    isLiveStream: false,
    emote: undefined,
    userId: '',
  },
  {
    videoId: 'presenter3',
    inUse: false,
    isLocal: false,
    videoMuted: false,
    audioMuted: false,
    isLiveStream: false,
    emote: undefined,
    userId: '',
  },
  {
    videoId: 'presenter4',
    inUse: false,
    isLocal: false,
    videoMuted: false,
    audioMuted: false,
    isLiveStream: false,
    emote: undefined,
    userId: '',
  },
];

const MAX_SLOTS = EMPTY_SLOTS.length;
const UNDEFINED_STREAM = { id: uuidv4(), stream: undefined };

@Component({
  selector: 'app-presentation-presenter-videos',
  templateUrl: './presentation-presenter-videos.component.html',
  styleUrls: ['./presentation-presenter-videos.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationPresenterVideosComponent extends Loggable implements AfterViewInit, OnDestroy {
  private deviceService = inject(DeviceService);
  private presentationService = inject(PresentationService);
  private videoRoomService = inject(VideoRoomService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private store = inject<Store<AppState>>(Store);
  private livestreamService = inject(LivestreamService);
  private favoriteService = inject(FavoriteService);
  private _snackBar = inject(MatSnackBar);
  private galleryService = inject(GalleryService);
  private badgingService = inject(BadgingService);
  private emoteService = inject(EmoteService);
  private browserService = inject(BrowserService);

  // TESTING LAYOUT, 1-4, 1 will not add test videos
  // public test = 1;
  // protected testArray = new Array(this.test - 1).fill(1);
  protected readonly stageSize = signal<StageSizeType>(StageSizeType.MEDIUM);

  public readonly presenterNameDisplayType = input(signal<PresenterNameDisplay>('always'));
  public readonly namePosition = input(signal<PresenterNamePosition>('top'));

  public isGalleryDrawerOpen$ = new BehaviorSubject<boolean>(false);
  private _isGalleryDrawerOpen = false;
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  @HostBinding('class.gallery-drawer-is-open')
  public set isGalleryDrawerOpen(value: boolean) {
    this._isGalleryDrawerOpen = value;
    this.isGalleryDrawerOpen$.next(value);
  }
  public get isGalleryDrawerOpen(): boolean {
    return this._isGalleryDrawerOpen;
  }

  public isSharingScreen$ = new BehaviorSubject<boolean>(false);
  private _isSharingScreen = false;
  private playPromise: Promise<void> | undefined;
  private emoteTimeout: string | number | Timeout | undefined;
  private emoteTimeoutDuration = 10000;
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  @HostBinding('class.screenshare')
  public set isSharingScreen(value: boolean) {
    this._isSharingScreen = value;
    this.isSharingScreen$.next(value);
  }
  public get isSharingScreen(): boolean {
    return this._isSharingScreen;
  }

  public readonly presentersDrawerOpenEmitter = output<boolean>();
  @HostBinding('class.screenshare-maximized')
  public readonly isScreenshareMaximized = computed(() => this.stageSize() === StageSizeType.FULL_SCREEN);

  private readonly _presentersDrawerOpen = signal<boolean>(true);
  @HostBinding('class.presenters-drawer-is-open')
  public get presentersDrawerOpenClass(): boolean {
    return this._presentersDrawerOpen();
  }
  public get presentersDrawerOpen(): boolean {
    return this._presentersDrawerOpen();
  }
  public set presentersDrawerOpen(value: boolean) {
    this._presentersDrawerOpen.set(value);
  }

  protected readonly isUserPresenting = signal<boolean>(false);

  public readonly slots = signal<PresenterSlot[]>(EMPTY_SLOTS);
  public readonly isHover = signal<boolean>(false);
  private readonly presenters = viewChildren<ElementRef<HTMLVideoElement>>('presenter');
  protected readonly presentationMyGroupV1 = signal<boolean>(false);

  private readonly socialId = signal<string | undefined>(undefined);
  private userVideoStreamSubscriptions = new Map<string, Subscription>();

  private readonly clickedOnBadge = signal<boolean>(false);

  protected readonly isPopout = signal<PopOutState>({ mode: 'PRESENTER', open: false });
  protected readonly clickedPresenter = signal<number | null>(null);
  protected readonly popOutVideo = signal<boolean>(false);

  protected readonly ringColors = signal<RingColor[]>([]);

  protected readonly localUserId = signal<string>('');

  private readonly baseUsersList = signal<BaseUser[]>([]);

  private destroyed$ = new ReplaySubject<boolean>(1);
  protected readonly activeSlots = signal<number>(0);
  private readonly badgeNames = signal<Record<string, string>>({});

  private readonly activeSpeakersPresenters = signal<string[]>([]);
  public readonly isMobile = signal<boolean>(false);
  private readonly presentersDetails = signal<ExtendedPresenter[]>([]);

  // Computed signals for UI helpers
  protected readonly presentersCount = computed(() => this.slots().filter((slot) => slot.inUse).length);
  protected readonly roomHasRingColors = computed(() => this.ringColors().length > 0);
  protected readonly isPresenterPopout = computed(() => this.isPopout().mode === 'PRESENTER' && this.isPopout().open);

  protected readonly showPresenterName = computed(() => {
    if (this.presenterNameDisplayType()() === 'never') {
      return false;
    }
    if (this.presenterNameDisplayType()() === 'hover') {
      return this.isHover();
    }
    return true;
  });

  public constructor() {
    super();
    this.setDisplayName('PresenterVideoComponent');

    this.store
      .select(socialAppSelectors.getJoinedSocialId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialId) => {
        this.socialId.set(socialId);
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => {
        this.isUserPresenting.set(isUserPresenting);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_my_group_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentationMyGroupV1) => {
        this.presentationMyGroupV1.set(presentationMyGroupV1);
      });

    this.store
      .select(socialAppSelectors.getPresentationStageSize)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (stageSize: StageSizeType) => {
          this.stageSize.set(stageSize);
        },
      });

    this.store
      .select(socialAppSelectors.isPopOut)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPopOut) => {
        this.isPopout.set(isPopOut);
      });

    this.store
      .select(socialAppSelectors.getVisualIndicatorInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visualIndicatorInfo) => {
        if (visualIndicatorInfo.colors) {
          this.ringColors.set(visualIndicatorInfo.colors);
        }
      });

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe(({ userId }) => {
        this.localUserId.set(userId ?? '');
      });

    combineLatest([
      this.galleryService.allBaseUsers$,
      this.store.select(socialAppSelectors.getPresenterDetails),
      this.isSharingScreen$,
      this.isGalleryDrawerOpen$,
    ])
      .pipe(takeUntil(this.destroyed$))
      // wait for getPresenterDetails to avoid race condition
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe(([baseUsersList, presenterDetails, isSharingScreen, isGalleryDrawerOpen]) => {
        this.baseUsersList.set(baseUsersList);
        this.presentersDetails.set(presenterDetails);
        // all active presenters + screen share
        if (!this.isMobile()) {
          const slots = this.slots().map((presenter) => {
            return {
              ...presenter,
              ringColor: this.getUserRingColor(presenter.userId || '', true),
            };
          });
          this.slots.set(slots);
        } else {
          this.handlePresenterChange(this.presentersDetails());
        }
      });

    this.badgingService.badgeNames$.pipe(takeUntil(this.destroyed$)).subscribe((badgeNames) => {
      this.badgeNames.set(badgeNames);
    });

    this.store
      .select(socialAppSelectors.getActivePresentationSpeakers)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((activeSpeakersPresenters) => {
        if (this.isMobile()) {
          this.activeSpeakersPresenters.set(activeSpeakersPresenters);
          //re-arrange presenters based on speaking presenters
          this.handlePresenterChange(this.presentersDetails());
        }
      });

    this.emoteService.emoteStream$.pipe(takeUntil(this.destroyed$)).subscribe((emote) => {
      if (!emote.userId) {
        return;
      }

      const presenterEmoji = this.slots().find((presenter) => presenter.userId === emote.userId);
      if (presenterEmoji) {
        this.updateEmote(presenterEmoji, emote);
      }
    });

    // PresentationPresenterVideosComponent.instance = this;
  }

  private updateEmote(presenterEmoji: PresenterSlot, userEmote: UserEmote) {
    clearTimeout(this.emoteTimeout);
    const index = this.slots().indexOf(presenterEmoji);
    const updatedSlots = [...this.slots()];
    updatedSlots[index].emote = userEmote.emote;
    this.slots.set(updatedSlots);

    this.emoteTimeout = setTimeout(() => {
      const currentSlots = [...this.slots()];
      currentSlots[index].emote = undefined;
      this.slots.set(currentSlots);
    }, this.emoteTimeoutDuration);
  }

  private calculateActiveSlots(): void {
    const activeCount = this.slots().filter(({ inUse }) => inUse).length + (this.isSharingScreen$.getValue() ? 1 : 0);
    this.activeSlots.set(activeCount);
  }

  public ngAfterViewInit(): void {
    // Defer store subscription until the DOM is ready
    this.store
      .select(socialAppSelectors.getPresenterDetails)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroyed$))
      .subscribe((presenterDetails) => {
        this.presentersDetails.set(presenterDetails);
        this.handlePresenterChange(presenterDetails);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pop_out_video)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((popOutVideo) => {
        this.popOutVideo.set(popOutVideo);
      });

    this.isMobile.set(this.browserService.isMobile());

    this.favoriteService.favoriteListStream$.pipe(takeUntil(this.destroyed$)).subscribe((favoriteList) => {
      const slots = this.slots().map((presenter) => {
        return {
          ...presenter,
          isFavorite: favoriteList.includes(presenter.userId || ''),
        };
      });
      this.slots.set(slots);
    });
  }

  public get thumbprintType(): PresentationThumbprintTypes {
    return this.isSharingScreen ? PresentationThumbprintTypes.INLINE : PresentationThumbprintTypes.ABSOLUTE;
  }

  public ngOnDestroy(): void {
    this.clearStreamSubscriptions();
    this.isSharingScreen$.unsubscribe();
    this.presenters()?.forEach((presenter) => {
      presenter.nativeElement.srcObject = null;
    });
    this.slots.set(EMPTY_SLOTS);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  @HostBinding('class.screenshare-maximized')
  public get isSharingScreenMaximized(): boolean {
    return !!PresentationPresenterSharingComponent.getInstance()?.isFullScreen();
  }

  private clearStreamSubscriptions(): void {
    this.userVideoStreamSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.userVideoStreamSubscriptions.clear();
  }

  public toggleVideoDrawer($event: Event): void {
    // If clicked on the badge should not let drawer to open
    if (this.clickedOnBadge()) {
      this.clickedOnBadge.set(false);
      return;
    }
    // don't double toggle when clicking on the button inside the drawer
    $event.stopPropagation();
    // only collapsible when screensharing
    if (!this.isSharingScreen) {
      return;
    }
    this.presentersDrawerOpen = !this.presentersDrawerOpen;
    this.presentersDrawerOpenEmitter.emit(this.presentersDrawerOpen);
  }

  private async handleMediaStreamChange({
    id,
    stream,
    videoId,
  }: {
    stream?: MediaStream;
    id: string;
    videoId: string;
  }): Promise<void> {
    const videoElement = this.getVideoElement(videoId);
    if (!videoElement) {
      this.warn('handleMediaStreamChange: Failed to get video element');
      return;
    }

    // If a play promise is pending, wait for it to resolve before loading a new source
    if (this.playPromise) {
      try {
        await this.playPromise;
      } catch {
        // Handle error
      }
    }

    // Avoid resetting the video source if this media stream has already been set. This avoids video flicker when a
    // different presenter joins or mutes their video
    if (videoElement.getAttribute('data-stream-id') === id) {
      this.trace('handleMediaStreamChange: skipping video stream update, stream already set as source');
      return;
    }

    videoElement.srcObject = stream ?? null;
    videoElement.setAttribute('data-stream-id', id);

    if (!videoElement.oncanplay) {
      videoElement.oncanplay = async () => {
        videoElement.oncanplay = null;
        try {
          this.playPromise = videoElement.play();
          await this.playPromise;
          videoElement.muted = true;
        } catch (e) {
          this.error('handleMediaStreamChange: Failed to play video', e);
        }
      };
    }
  }

  public getVideoIdByUserId = (userId: string) => {
    //Get the videoId in the original slot position
    return this.slots().find((item) => item.userId === userId)?.videoId;
  };

  public calcShowSlots = () => {
    //Check for maximum presenters to show based on conditions
    if (this.isMobile()) {
      if (this.isSharingScreen) {
        return 0;
      } else {
        if (this._isGalleryDrawerOpen) {
          return 1;
        } else {
          return 2;
        }
      }
    }
    return MAX_SLOTS - 1;
  };

  public getSlotItem = (index: number) => {
    return this.slots()[index];
  };

  // For mobile only, get the intended presenter position on array based on speaking presenters and video availability
  private getUserOrderIndex = (userId: string, presenters: ExtendedPresenter[]): number | null => {
    if (!userId) {
      return null;
    }
    const indexInSpeakers = this.activeSpeakersPresenters().indexOf(userId);
    if (indexInSpeakers !== -1) {
      // Priority to the speakers, add then first
      return indexInSpeakers;
    }
    // If user is not on speaking presenters, find the id in the presenters array, but ignore the muted ones
    const filteredPresenters = presenters.filter((presenter) => presenter.userId && presenter.videoMuted === false);
    const indexInSlots = filteredPresenters.findIndex((presenter) => presenter.userId === userId);

    if (indexInSlots !== -1) {
      // Relative position after prioritary speakers
      return this.activeSpeakersPresenters().length + indexInSlots;
    }

    return null;
  };

  /**
   * Handle changes to the presenter list. This will ensure presenters video shows up in the display as we receive a
   * media stream from them.
   * @param presenters The presenters to display
   * @returns
   */
  private handlePresenterChange = (presenters: ExtendedPresenter[]) => {
    // Clear previous state. On each presenter change, we'll re-add the stream subscriptions.
    const updatedSlots = this.slots().map((slot) => ({ ...slot, inUse: false }));
    this.slots.set(updatedSlots);
    this.clearStreamSubscriptions();

    if (presenters.length > MAX_SLOTS) {
      this.fatal('Unsupported number of presenters', { maxPresenters: MAX_SLOTS, presenters });
      return;
    }

    // Assign presenters to slots based on their index
    presenters.forEach((presenter, index) => {
      const { isLocal, presenterType, socialId, userId } = presenter;
      let slot = { ...EMPTY_SLOTS[index] };

      //Set default values for the index
      const orderedIndex = this.getUserOrderIndex(userId, presenters);
      let sortedIndex = index;

      //For mobile only, set the slot in the sorted position based on speaking participants
      if (this.isMobile()) {
        if (orderedIndex !== null) {
          sortedIndex = orderedIndex;
          slot = { ...EMPTY_SLOTS[sortedIndex] };
        }
      }

      const { videoId } = slot;
      // Local presenters don't provide a social ID, though we should also check that the social ID is different
      const isCrossRoom = socialId !== this.socialId();
      let subscription: Subscription;
      // Local presenters pull their media stream directly from the camera via device service.
      if (isLocal) {
        subscription = this.createLocalVideoSubscription(videoId);
      } else if (isCrossRoom) {
        subscription = this.createCrossRoomVideoSubscription(videoId, presenter);
      } else if (presenterType === PresenterType.LIVE_STREAM) {
        subscription = this.createLivestreamVideoSubscription(videoId, userId);
      } else {
        subscription = this.createParticipantVideoSubscription(videoId, userId);
      }

      this.userVideoStreamSubscriptions.set(userId, subscription);

      const playerRingColor = this.getUserRingColor(presenter.userId, isCrossRoom);
      const toShowIndexes = this.calcShowSlots();

      let isSlotInUse = true;

      //For mobile only, if the defined index is bigger than the maximum of presenters per situation, set as inUse=false
      //This will insure that the presenters will be ordered, and not show more than supported.
      if (this.isMobile()) {
        if (orderedIndex == null || sortedIndex > toShowIndexes || presenter.videoMuted) {
          isSlotInUse = false;
        }
      }

      slot = {
        ...slot,
        inUse: isSlotInUse,
        isLocal,
        isLiveStream: presenterType === PresenterType.LIVE_STREAM,
        name: formatUserName(presenter),
        userId: presenter.userId,
        ringColor: playerRingColor,
        videoMuted: presenter.videoMuted,
        audioMuted: presenter.audioMuted ?? false,
      };

      this.slots()[sortedIndex] = slot;
      this.calculateActiveSlots();

      this.info('EXITING SLOTS', this.slots);
    });

    // Ensure inactive slots do not have a video stream
    this.slots()
      .filter(({ inUse }) => !inUse)
      .forEach(({ videoId }) => {
        const videoElement = this.getVideoElement(videoId);
        if (videoElement) {
          videoElement.setAttribute('data-stream-id', '');
          videoElement.srcObject = null;
        }
      });
    this.changeDetectorRef.markForCheck();
  };

  private getVideoElement(videoId: string): HTMLVideoElement | undefined {
    return this.presenters()?.find((presenter) => {
      return presenter.nativeElement.getAttribute('data-presenterid') === videoId;
    })?.nativeElement;
  }

  /**
   * Create a subscription to the local user's media stream.
   *
   * @param videoId The video element id to display the stream in
   * @returns A subscription to the stream
   */
  private createLocalVideoSubscription(videoId: string): Subscription {
    return this.deviceService.cameraStream$
      .pipe(
        map((stream) => (stream ? { stream, id: stream.id } : UNDEFINED_STREAM)),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange({ id, stream, videoId }));
  }

  /**
   *  Create a subscription to a cross-room user's media stream.
   *
   * @param videoId The video element id to display the stream in
   * @param presenter The presenter to pull the stream from
   * @returns A subscription to the stream
   */
  private createCrossRoomVideoSubscription(videoId: string, presenter: Presenter): Subscription {
    // The media stream observable provides produces a value for any mirror change. Filter for the mirror associated
    // with this user.
    const { presenterType, userId } = presenter;
    const mirrorId = createMirrorId({ userId }, MirrorType.PUBLISHER);
    return this.presentationService.mirrorMediaStreams$
      .pipe(
        map((streams: MirrorMediaStreams) => {
          const { video: stream, videoId: id } =
            streams[presenterType === PresenterType.LIVE_STREAM ? userId : mirrorId] ?? {};
          return { id, stream };
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange({ stream, id, videoId }));
  }

  /**
   * Create a subscription to a livestream's media stream.
   *
   * @param videoId The video element id to display the stream in
   * @param streamId The livestream id to pull the stream from
   * @returns A subscription to the stream
   */
  private createLivestreamVideoSubscription(videoId: string, streamId: string): Subscription {
    return this.livestreamService.livestreamMediaStream$
      .pipe(
        map((streams: LivestreamMediaStreams) => {
          const { video: stream, videoId: id } = streams[streamId] ?? {};
          return { id, stream };
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange({ stream, id, videoId }));
  }

  /**
   *  Create a subscription to an in-room user's media stream.
   *
   * @param videoId The video element id to display the stream in
   * @param userId The user id of the participant to pull the stream from
   * @returns A subscription to the stream
   */
  private createParticipantVideoSubscription(videoId: string, userId: string): Subscription {
    // Remote presenters pull their media stream from the video room service.
    return this.videoRoomService.participantVideoStream$
      .pipe(
        map((streams) => streams[userId] ?? UNDEFINED_STREAM),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange({ stream, id, videoId }));
  }

  public formatPresentersName(user: User): string {
    // Allow badge override
    const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
    const username = name ? UserUtil.trimName(20, name) : '';
    if (!user.userId) {
      return username;
    }

    const badgeNames = this.badgeNames();
    const badgeName = badgeNames[user.userId];

    if (!badgeName || typeof badgeName !== 'string') {
      return username;
    }
    return badgeName.trim();
  }

  public openBadge(userId?: string): void {
    this.clickedOnBadge.set(true);
    this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: userId ? userId : '' }));
  }

  protected addOrRemoveFavorite({ isFavorite, name, userId }: PresenterSlot) {
    if (isFavorite) {
      this.favoriteService.unfavoriteParticipant(this.localUserId(), userId);
    } else {
      this.favoriteService.favoriteParticipant(this.localUserId(), userId);
    }
    this.openFavoriteNotification(!isFavorite, name);
  }

  private openFavoriteNotification(favorited: boolean, name?: string, duration = 2000): void {
    const _data: SnackBarData = {
      message: `${name ?? ''} has been ${favorited ? 'added to' : 'removed from'} your favorites`,
      type: 'success',
      icon: 'FAVORITE_STAR_FILL_SUCCESS',
      mode: this.presentationService.isPresentationActive ? 'dark' : 'light',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  private getUserRingColor(userId: string, isCrossRoom: boolean) {
    let playerRingColor: string | undefined;
    if (this.roomHasRingColors()) {
      playerRingColor = this.videoRoomService.getPresentersRingColor(userId);

      if (isCrossRoom || !playerRingColor) {
        const baseUsers = this.baseUsersList();
        const baseUser = baseUsers.find((user) => user.userId === userId);
        if (baseUser && baseUser.ringColor) {
          playerRingColor = baseUser.ringColor;
        }
      }
    }
    return playerRingColor;
  }

  protected get collapseDrawerButtonText() {
    if (this.presentersDrawerOpen) {
      return this.presentersCount() > 1 ? 'Hide presenters' : 'Hide presenter';
    }
    return this.presentersCount() > 1 ? 'Show Presenters' : 'Show Presenter';
  }
}
